import React, { useContext, useMemo } from 'react';

import Alert from 'kingpin/Alert';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Form from 'kingpin/forms/Form';
import FormContent from 'kingpin/forms/FormContent';
import FormHeader from 'kingpin/forms/FormHeader';
import FormInput from 'kingpin/forms/FormInput';

import Row from 'components/Common/Row';
import Inputs from 'components/Inputs';
import Loading from 'components/Loading';
import MultiGroupSelect from 'components/MultiGroupSelect';
import MultiUserSelect from 'components/MultiUserSelect';
import { GroupsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/GroupsProvider';
import useReportAccessForUsers from 'hooks/useReportAccessForUsers';

const FREQUENCY_OPTIONS: DropdownOption[] = [
  {
    label: 'Daily',
    value: 'daily',
    isSelected: false,
    onSelected: () => {},
  },
  {
    label: 'Weekly',
    value: 'weekly',
    isSelected: false,
    onSelected: () => {},
  },
  {
    label: 'Monthly',
    value: 'monthly',
    isSelected: false,
    onSelected: () => {},
  },
];

const DAY_OF_WEEK_OPTIONS: DropdownOption[] = [
  { label: 'Monday', value: '1', isSelected: false, onSelected: () => {} },
  { label: 'Tuesday', value: '2', isSelected: false, onSelected: () => {} },
  { label: 'Wednesday', value: '3', isSelected: false, onSelected: () => {} },
  { label: 'Thursday', value: '4', isSelected: false, onSelected: () => {} },
  { label: 'Friday', value: '5', isSelected: false, onSelected: () => {} },
  { label: 'Saturday', value: '6', isSelected: false, onSelected: () => {} },
  { label: 'Sunday', value: '0', isSelected: false, onSelected: () => {} },
];

const DAY_OF_MONTH_OPTIONS: DropdownOption[] = Array.from(
  { length: 31 },
  (_, i) => ({
    label: `${i + 1}`,
    value: `${i + 1}`,
    isSelected: false,
    onSelected: () => {},
  }),
);

const AccessWarning = ({
  content,
  recipientUserIds,
  recipientGroupIds,
}: {
  content: ReportSchedules.ScheduleContent;
  recipientUserIds: string[];
  recipientGroupIds: string[];
}) => {
  const { groups } = useContext(GroupsContext);
  const hasAccess = useReportAccessForUsers({ content });

  const blockedCount = useMemo(() => {
    const groupMemberIds = recipientGroupIds.flatMap((gid) => {
      const group = groups.find((g) => g.id === gid);
      return group?.userIds ?? [];
    });
    const effectiveIds = Array.from(
      new Set([...recipientUserIds, ...groupMemberIds]),
    );
    return effectiveIds.filter((uid) => !hasAccess(uid)).length;
  }, [groups, hasAccess, recipientGroupIds, recipientUserIds]);

  if (blockedCount === 0) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <Alert
        type="Warning"
        body={`${blockedCount} recipient${blockedCount === 1 ? '' : 's'} without access to this ${content.type} won't receive it.`}
      />
    </div>
  );
};

const ScheduleReportModal = ({
  existingSchedule,
  content,
  recipientUserIds,
  setRecipientUserIds,
  recipientGroupIds,
  setRecipientGroupIds,
  notes,
  onNotesChanged,
  frequency,
  setFrequency,
  timeOfDay,
  onTimeOfDayChanged,
  dayOfWeek,
  setDayOfWeek,
  dayOfMonth,
  setDayOfMonth,
  timeZone,
  isLoading,
  onSaveClicked,
  onDeleteClicked,
  runHistory,
  close,
}: {
  existingSchedule: ReportSchedules.Schedule | undefined;
  content?: ReportSchedules.ScheduleContent;
  recipientUserIds: string[];
  setRecipientUserIds: React.Dispatch<React.SetStateAction<string[]>>;
  recipientGroupIds: string[];
  setRecipientGroupIds: React.Dispatch<React.SetStateAction<string[]>>;
  notes: string;
  onNotesChanged: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  frequency: ReportSchedules.Cadence['frequency'];
  setFrequency: (f: ReportSchedules.Cadence['frequency']) => void;
  timeOfDay: string;
  onTimeOfDayChanged: (e: React.ChangeEvent<HTMLInputElement>) => void;
  dayOfWeek: number;
  setDayOfWeek: (d: number) => void;
  dayOfMonth: number;
  setDayOfMonth: (d: number) => void;
  timeZone: string;
  isLoading: boolean;
  onSaveClicked: () => void;
  onDeleteClicked: (() => void) | undefined;
  runHistory?: React.ReactNode;
  close: () => void;
}) => {
  const isSaveDisabled =
    (recipientUserIds.length === 0 && recipientGroupIds.length === 0) ||
    isLoading;

  const frequencyOptions = FREQUENCY_OPTIONS.map((o) => ({
    ...o,
    isSelected: o.value === frequency,
    onSelected: () =>
      setFrequency(o.value as ReportSchedules.Cadence['frequency']),
  }));

  const selectedFrequencyLabel =
    FREQUENCY_OPTIONS.find((o) => o.value === frequency)?.label ?? '';

  const dayOfWeekOptions = DAY_OF_WEEK_OPTIONS.map((o) => ({
    ...o,
    isSelected: o.value === String(dayOfWeek),
    onSelected: () => setDayOfWeek(Number(o.value)),
  }));

  const selectedDayOfWeekLabel =
    DAY_OF_WEEK_OPTIONS.find((o) => o.value === String(dayOfWeek))?.label ?? '';

  const dayOfMonthOptions = DAY_OF_MONTH_OPTIONS.map((o) => ({
    ...o,
    isSelected: o.value === String(dayOfMonth),
    onSelected: () => setDayOfMonth(Number(o.value)),
  }));

  const selectedDayOfMonthLabel = String(dayOfMonth);

  return (
    <Form>
      <FormHeader
        title={
          existingSchedule
            ? 'Edit Schedule'
            : `Schedule ${content?.type === 'dashboard' ? 'Dashboard' : 'Report'}`
        }
        onClose={close}
      />
      <FormContent>
        {isLoading && <Loading />}
        {!isLoading && (
          <>
            <FormInput label="Recipients">
              <MultiUserSelect
                selectedUsers={recipientUserIds}
                setSelectedUsers={setRecipientUserIds}
              />
            </FormInput>

            <FormInput label="Recipient Groups">
              <MultiGroupSelect
                selectedGroupIds={recipientGroupIds}
                setSelectedGroupIds={setRecipientGroupIds}
              />
            </FormInput>

            {content && (
              <AccessWarning
                content={content}
                recipientUserIds={recipientUserIds}
                recipientGroupIds={recipientGroupIds}
              />
            )}

            <FormInput label="Frequency">
              <Inputs.Dropdown
                options={frequencyOptions}
                selectedLabel={selectedFrequencyLabel}
                fullWidth
              />
            </FormInput>

            {frequency === 'weekly' && (
              <FormInput label="Day of week">
                <Inputs.Dropdown
                  options={dayOfWeekOptions}
                  selectedLabel={selectedDayOfWeekLabel}
                  fullWidth
                />
              </FormInput>
            )}

            {frequency === 'monthly' && (
              <FormInput label="Day of month">
                <Inputs.Dropdown
                  options={dayOfMonthOptions}
                  selectedLabel={selectedDayOfMonthLabel}
                  fullWidth
                />
              </FormInput>
            )}

            <FormInput label="Time of day">
              <input
                type="time"
                value={timeOfDay}
                onChange={onTimeOfDayChanged}
                style={{ width: '100%', padding: '6px 12px', borderRadius: 8 }}
              />
            </FormInput>

            <FormInput label="Timezone">
              <Typography.Body type="Body 12">{timeZone}</Typography.Body>
            </FormInput>

            <FormInput label="Note">
              <Inputs.TextArea
                onChange={onNotesChanged}
                value={notes}
                placeholder="Add a message"
                style={{ resize: 'none' }}
              />
            </FormInput>

            {runHistory}
          </>
        )}
      </FormContent>

      <Row spaceBetween>
        {onDeleteClicked ? (
          <Button
            size="Small"
            type="Secondary"
            label="Delete"
            onClick={onDeleteClicked}
            isDisabled={isLoading}
          />
        ) : (
          <div />
        )}
        <Row>
          <Button
            size="Small"
            type="Secondary"
            label="Cancel"
            onClick={close}
            isDisabled={isLoading}
          />
          <div style={{ marginLeft: 8 }} />
          <Button
            size="Small"
            type="Primary"
            label={existingSchedule ? 'Update' : 'Create'}
            onClick={onSaveClicked}
            isDisabled={isSaveDisabled}
            isLoading={isLoading}
          />
        </Row>
      </Row>
    </Form>
  );
};

export default ScheduleReportModal;
